import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/bills';

import {
  Modal,
  DropdownTagSelector,
  Input,
  StatusIndicator,
  SkeletonBase,
  formatAmountWithCommas,
} from '@unified-checkout/design-system';

import {
  MODAL_STEP_STATUS,
  BILLS_STATUS,
  ADS_TOP_UP_MODAL,
  CLOSE,
  GOOGLE,
} from '@app/dashboard/constants/Bills';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { DEBOUNCE_INPUT_TIME } from '@app/dashboard/constants/transfers';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';

import { confirmPayAdsTopUp } from '@app/dashboard/services/inner_layout/bills/pay_ads/confirm-top-up';
import { getTopUpDetails } from '@app/dashboard/services/inner_layout/bills/pay_ads/get-top-up-details';
import { getBillsBalance } from '@app/dashboard/services/inner_layout/bills/pay_ads/get-bills-balance';

import type { ITopUpModal } from '@app/dashboard/types/innerLayout/bills/top-up';

import { calcRemainingBalance, isStatusVisible } from '../../../utils';

import styles from './top-up.module.scss';

export const TopUpModal = ({
  showTopUp,
  setShowTopUp,
  accountDetails,
  selectedAccount
}: ITopUpModal) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>(MODAL_STEP_STATUS.CONFIRM);
  const [selectedMerchantOption, setSelectedMerchantOption] = useState<any>(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [debouncedValues, setDebouncedValues] = useState('');
  const [error, setError] = useState<string>('');

  const {
    data: availableBalanceData,
    isLoading: isAvailableBalanceLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BILLS_BALANCE],
    queryFn: getBillsBalance,
    enabled: showTopUp,
  });
  const {
    display_amount: availableBalanceDisplayAmount,
    currency: availableBalanceCurrency,
    title: availableBalanceBillstitle
  } = availableBalanceData?.data?.data || {}

  const LinkedAccounts = accountDetails?.data?.data?.account_details ? [accountDetails?.data?.data?.account_details] : [];
  const minAmount = accountDetails?.data?.data?.min_amount;
  const isMinAmountError = !!minAmount && Number(transferAmount) > 0 && Number(transferAmount) < Number(minAmount);
  const minAmountErrorMessage = isMinAmountError ? t('innerLayout.billsPage.transferModal.minAmountError', { minAmount: minAmount }) : '';
  const accountId = selectedAccount?.account_id;
  const hasValidAmount = !!(transferAmount) && Number(transferAmount) > 0 && !isMinAmountError;
  const isbillFeesValid = !!debouncedValues && Number(debouncedValues) > 0 && !isMinAmountError;

  const {
    data: topUpDetailsData,
    isFetching: isTopUpDetailsLoading,
    error: topUpDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_TOP_UP_DETAILS, debouncedValues],
    queryFn: () => getTopUpDetails({
      amount: Number(debouncedValues),
      account_id: accountId,
    }),
    enabled: showTopUp && isbillFeesValid && !!accountId,
  });

  const billDetails = isbillFeesValid
    ? (topUpDetailsData?.data?.data?.payment_details?.details ?? [])
    : [];
  const confirmPayload = topUpDetailsData?.data?.data?.bill_details;
  const insuffiecientBalance = topUpDetailsData?.data?.data?.insuffiecient_balance;

  const {
    mutate: confirmTopUp,
    isPending: isConfirmTopUpLoading,
    error: confirmTopUpError,
    data: confirmTopUpData
  } = useMutation({
    mutationFn: ({ payload }: { payload: any; trackingData: { accountId?: string; amount: string; remainingBillsBalance: string } }) => confirmPayAdsTopUp(payload),
    onSuccess: (response, variables) => {
      const trackedPayload = variables?.trackingData;
      amplitude.track(BILLS_EVENTS.BILLS_AD_ACCOUNT_TOP_UP_CONFIRMED, {
        [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
        [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: trackedPayload.accountId,
        [BILLS_EVENTS_PARAMS.AMOUNT]: trackedPayload.amount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: trackedPayload.remainingBillsBalance,
        [BILLS_EVENTS_PARAMS.BILL_STATUS]: response?.data?.status,
      });

      setStatus(MODAL_STEP_STATUS.SUCCESS);
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.GET_BILLS_BALANCE] });
    },
    onError: (mutationError: any, variables) => {
      const trackedPayload = variables?.trackingData;
      amplitude.track(BILLS_EVENTS.BILLS_AD_ACCOUNT_TOP_UP_CONFIRMED, {
        [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
        [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: trackedPayload.accountId,
        [BILLS_EVENTS_PARAMS.AMOUNT]: trackedPayload.amount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: trackedPayload.remainingBillsBalance,
        [BILLS_EVENTS_PARAMS.BILL_STATUS]: mutationError?.response?.data?.status,
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: mutationError?.response?.data?.message,
      });

      setStatus(MODAL_STEP_STATUS.FAILED);
    },
  });

  const handleClose = () => {
    if (!showTopUp) {
      return;
    }

    setShowTopUp(false);
    setStatus(MODAL_STEP_STATUS.CONFIRM);
    setTransferAmount('');
    setDebouncedValues('');
    setError('');

    amplitude.track(BILLS_EVENTS.BILLS_SCREEN_EXITED, {
      [BILLS_EVENTS_PARAMS.SCREEN_NAME]: ADS_TOP_UP_MODAL,
      [BILLS_EVENTS_PARAMS.EXIT_TYPE]: CLOSE,
    });
  };

  const handleConfirm = () => {
    const transferAmountNumber = Number(transferAmount);
    const availableBalanceNumber = Number(availableBalanceDisplayAmount);

    const trackingData = {
      accountId,
      amount: formatAmountWithCommas({ amount: transferAmountNumber }),
      remainingBillsBalance: calcRemainingBalance(transferAmountNumber, availableBalanceNumber),
    };

    confirmTopUp({ payload: confirmPayload, trackingData });
  };

  const handleAmountChange = (value: string) => {
    setTransferAmount(value);
  };

  const accountOptions = LinkedAccounts?.map((account: any) => ({
    id: String(account?.account_email ?? ""),
    label: account?.account_email ?? "",
    value: account,
    searchKey: account?.account_email ?? "",
  }));

  const topUpStatusContent = BILLS_STATUS().find((item) => item.id === status);

  useEffect(() => {
    if (accountOptions && accountOptions.length > 0 && !selectedMerchantOption) {
      setSelectedMerchantOption(accountOptions[0]);
    }
  }, [accountOptions, selectedMerchantOption]);

  useEffect(() => {
    if (!hasValidAmount || !accountId) {
      setDebouncedValues('');
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedValues(transferAmount);
    }, DEBOUNCE_INPUT_TIME);

    return () => clearTimeout(timeout);
  }, [transferAmount, selectedMerchantOption, hasValidAmount, accountId]);

  useEffect(() => {
    if (showTopUp) {
      amplitude.track(BILLS_EVENTS.BILLS_ADS_TOP_UP_PAGE_VIEWED, {
        [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
        [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: accountId,
      });
    }
  }, [showTopUp, accountId]);

  useEffect(() => {
    if (!topUpDetailsData) return;

    setError(
      topUpDetailsData?.data?.data?.insuffiecient_balance
        ? t('innerLayout.billsPage.transferModal.insuffiecientBalance')
        : ''
    );
  }, [topUpDetailsData, t]);

  const getStatusContent = () => {
    if (status === MODAL_STEP_STATUS.SUCCESS) {
      return {
        title: confirmTopUpData?.data?.title,
        description: confirmTopUpData?.data?.description,
      };
    }

    if (status === MODAL_STEP_STATUS.FAILED) {
      return {
        title: (confirmTopUpError as any)?.response?.data?.title,
        description: (confirmTopUpError as any)?.response?.data?.message || (confirmTopUpError as any)?.response?.data?.description,
      };
    }

    return { title: '', description: '' };
  };

  const { title: statusTitle, description: statusDescription } = getStatusContent();

  const amountError = minAmountErrorMessage ||
    (topUpDetailsError as any)?.response?.data?.data?.amount?.[0] ||
    (topUpDetailsError as any)?.response?.data?.message;

  const isConfirmDisabled = insuffiecientBalance ||
    !transferAmount ||
    Number(transferAmount) <= 0 ||
    isMinAmountError ||
    isTopUpDetailsLoading ||
    !!topUpDetailsError ||
    isConfirmTopUpLoading;

  const modalBtnsProps = !isStatusVisible(status) ?
    [
      {
        text: t('innerLayout.billsPage.transferModal.confirmButton'),
        onClickHandler: () => handleConfirm(),
        disabled: isConfirmDisabled,
        loading: isConfirmTopUpLoading,
      },
      {
        color: "secondary" as const,
        variant: "outlined" as const,
        text: t('innerLayout.billsPage.transferModal.cancelButton'),
        onClickHandler: handleClose,
      },
    ]
    : undefined;

  const loader = ({
    section,
    height,
    width,
    key,
    noOfLoader,
    isLoading,
    hasKeyAndValue
  }: {
    section: React.ReactNode;
    height?: string;
    width?: string;
    key: string;
    isLoading: boolean;
    noOfLoader?: number;
    hasKeyAndValue?: boolean;
  }): React.ReactNode => {
    return isLoading ? (
      <>
        {hasKeyAndValue ?
          <div>
            {Array.from({ length: noOfLoader ?? 1 }, (_, index) =>
              <div className={styles['modal__body__fees-info__item']} key={index}>
                <SkeletonBase height={height} width={width} />
                <SkeletonBase height={height} width={width} />
              </div>
            )}
          </div>
          :
          Array.from({ length: noOfLoader ?? 1 }, (_, index) =>
            <SkeletonBase
              height={height}
              width={width}
              key={`${key}-${index}`}
            />
          )}
      </>
    ) : section;
  };

  return (
    <div className={styles['modal']}>
      <Modal
        state="primary"
        title={!isStatusVisible(status) && t('innerLayout.billsPage.addFundsModal.instantTopUpTitle')}
        hasCloseIcon
        closeOnClickOutside={false}
        open={showTopUp}
        onCloseHandler={handleClose}
        position="center"
        errorMsg={error}
        btnsProps={modalBtnsProps}
      >
        <div className={styles['modal__body']}>
          {isStatusVisible(status) ? (
            topUpStatusContent &&
            <StatusIndicator
              status={topUpStatusContent.status}
              icon={topUpStatusContent.icon}
              text={statusTitle}
              hintText={statusDescription}
            />
          )
            :
            <>
              <DropdownTagSelector
                isMultiple={false}
                isResetSearchQueryOnToggle={false}
                dropdownProps={{
                  label: t('innerLayout.billsPage.topUpModal.selectLinkedAccount'),
                }}
                optionsList={accountOptions}
                selectedOptions={selectedMerchantOption ? [selectedMerchantOption] : []}
                onSelectionChange={(items) => setSelectedMerchantOption(items[0] ?? null)}
                tagsInputProps={{
                  name: 'Top-up-linked-account',
                  placeholder: t('innerLayout.billsPage.topUpModal.selectLinkedAccount'),
                }}
              />

              <div className={styles['modal__body__amount-input']}>
                <Input
                  name="bills-transfer-amount"
                  label={t('innerLayout.billsPage.transferModal.amount')}
                  type="number"
                  leadingText='EGP'
                  placeholder='ex. 1,999'
                  value={transferAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  disabled={isAvailableBalanceLoading}
                  error={amountError}
                  status={amountError ? 'error' : 'default'}
                />
                {loader({
                  key: 'balance-info',
                  height: '1rem',
                  width: '7rem',
                  isLoading: isAvailableBalanceLoading,
                  section: !isAvailableBalanceLoading &&
                    <div className={styles['modal__body__amount-input__balance']}>
                      <div className={styles['modal__body__amount-input__balance__label']}>
                        {availableBalanceBillstitle}:
                      </div>
                      <div className={styles['modal__body__amount-input__balance__value']}>
                        {availableBalanceCurrency} {formatAmountWithCommas({ amount: Number(availableBalanceDisplayAmount) })}
                      </div>
                    </div>
                })
                }
              </div>
              <div className={styles['modal__body__fees-info']}>
                {loader({
                  key: 'fees-info',
                  width: '5rem',
                  height: '1rem',
                  isLoading: isTopUpDetailsLoading,
                  hasKeyAndValue: true,
                  noOfLoader: 6,
                  section: (
                    <>
                      {billDetails?.map((fee: any, index: number) => (
                        <div className={styles['modal__body__fees-info__item']} key={index}>
                          <div className={styles['modal__body__fees-info__item__key']}>
                            {fee.title || fee.key}
                          </div>
                          <div className={styles['modal__body__fees-info__item__value']}>
                            {fee.value || '-'}
                          </div>
                        </div>
                      ))}
                    </>
                  ),
                })}
              </div>
            </>
          }
        </div>
      </Modal>
    </div>
  );
};

